import {
  CHUNK_LOAD_RECOVERY_COOKIE_KEY,
  CHUNK_LOAD_RECOVERY_COOLDOWN_MS,
  CHUNK_LOAD_RECOVERY_STORAGE_KEY,
  CHUNK_LOAD_RECOVERY_URL_PARAM,
} from './chunk-load-constants';

let memoryAttemptAt: number | null = null;
let sessionStorageBlocked: boolean | null = null;
let cookieBlocked: boolean | null = null;
let urlBlocked: boolean | null = null;

const resetCapabilityCache = (): void => {
  sessionStorageBlocked = null;
  cookieBlocked = null;
  urlBlocked = null;
};

const parseTimestamp = (raw: string | null | undefined): number | null => {
  if (!raw) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

type SessionRead =
  { available: true; value: number | null } | { available: false };

const readSession = (): SessionRead => {
  if (sessionStorageBlocked) {
    return { available: false };
  }
  try {
    const value = parseTimestamp(
      sessionStorage.getItem(CHUNK_LOAD_RECOVERY_STORAGE_KEY)
    );
    return { available: true, value };
  } catch {
    sessionStorageBlocked = true;
    return { available: false };
  }
};

/**
 * A successful getItem is not enough: some private modes allow reads and
 * throw on setItem. Probe a write before offering automatic reload.
 */
const sessionStorageWritable = (): boolean => {
  if (sessionStorageBlocked !== null) {
    return !sessionStorageBlocked;
  }
  const probeKey = `${CHUNK_LOAD_RECOVERY_STORAGE_KEY}-probe`;
  try {
    sessionStorage.setItem(probeKey, '1');
    const stored = sessionStorage.getItem(probeKey) === '1';
    try {
      sessionStorage.removeItem(probeKey);
    } catch {
      // The probe value stuck. Leaving the key behind does not affect cooldown.
    }
    sessionStorageBlocked = !stored;
    return stored;
  } catch {
    sessionStorageBlocked = true;
    return false;
  }
};

const writeSession = (now: number): boolean => {
  if (sessionStorageBlocked) {
    return false;
  }
  try {
    sessionStorage.setItem(CHUNK_LOAD_RECOVERY_STORAGE_KEY, String(now));
    const stored =
      sessionStorage.getItem(CHUNK_LOAD_RECOVERY_STORAGE_KEY) === String(now);
    sessionStorageBlocked = !stored;
    return stored;
  } catch {
    sessionStorageBlocked = true;
    return false;
  }
};

const readCookie = (): number | null => {
  try {
    const parts = document.cookie.split(';');
    for (const part of parts) {
      const separator = part.indexOf('=');
      if (separator === -1) {
        continue;
      }
      const name = part.slice(0, separator).trim();
      if (name !== CHUNK_LOAD_RECOVERY_COOKIE_KEY) {
        continue;
      }
      return parseTimestamp(part.slice(separator + 1).trim());
    }
    return null;
  } catch {
    return null;
  }
};

const cookieSecureSuffix = (): string =>
  window.location.protocol === 'https:' ? '; Secure' : '';

const writeCookie = (now: number): boolean => {
  if (cookieBlocked) {
    return false;
  }
  const maxAgeSeconds = Math.max(
    1,
    Math.ceil(CHUNK_LOAD_RECOVERY_COOLDOWN_MS / 1000)
  );
  try {
    document.cookie = `${CHUNK_LOAD_RECOVERY_COOKIE_KEY}=${now}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${cookieSecureSuffix()}`;
    const stored = readCookie() === now;
    cookieBlocked = !stored;
    return stored;
  } catch {
    cookieBlocked = true;
    return false;
  }
};

const clearCookie = (): void => {
  try {
    document.cookie = `${CHUNK_LOAD_RECOVERY_COOKIE_KEY}=; path=/; max-age=0; SameSite=Lax${cookieSecureSuffix()}`;
  } catch {
    // Cookie access can throw in locked-down browsers.
  }
};

const readUrl = (): number | null => {
  try {
    return parseTimestamp(
      new URL(window.location.href).searchParams.get(
        CHUNK_LOAD_RECOVERY_URL_PARAM
      )
    );
  } catch {
    return null;
  }
};

const replaceUrl = (mutate: (params: URLSearchParams) => void): boolean => {
  try {
    const url = new URL(window.location.href);
    mutate(url.searchParams);
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, '', next);
    return true;
  } catch {
    return false;
  }
};

const writeUrl = (now: number): boolean => {
  if (urlBlocked) {
    return false;
  }
  const wrote = replaceUrl((params) => {
    params.set(CHUNK_LOAD_RECOVERY_URL_PARAM, String(now));
  });
  const stored = wrote && readUrl() === now;
  urlBlocked = !stored;
  return stored;
};

const clearUrl = (): void => {
  if (!readUrl()) {
    return;
  }
  replaceUrl((params) => {
    params.delete(CHUNK_LOAD_RECOVERY_URL_PARAM);
  });
};

const cookieAvailable = (): boolean => {
  if (cookieBlocked !== null) {
    return !cookieBlocked;
  }
  const probeName = `${CHUNK_LOAD_RECOVERY_COOKIE_KEY}-probe`;
  try {
    document.cookie = `${probeName}=1; path=/; max-age=60; SameSite=Lax${cookieSecureSuffix()}`;
    const stored = document.cookie.split(';').some((part) => {
      return part.trim().startsWith(`${probeName}=`);
    });
    document.cookie = `${probeName}=; path=/; max-age=0; SameSite=Lax${cookieSecureSuffix()}`;
    cookieBlocked = !stored;
    return stored;
  } catch {
    cookieBlocked = true;
    return false;
  }
};

const urlAvailable = (): boolean => {
  if (urlBlocked !== null) {
    return !urlBlocked;
  }
  try {
    window.history.replaceState(window.history.state, '', window.location.href);
    urlBlocked = false;
    return true;
  } catch {
    urlBlocked = true;
    return false;
  }
};

const canPersistRecoveryMarker = (): boolean => {
  if (sessionStorageWritable()) {
    return true;
  }
  if (cookieAvailable()) {
    return true;
  }
  return urlAvailable();
};

/**
 * Timestamp of the last recovery reload.
 * sessionStorage is preferred when it actually holds a value. Cookie, then
 * the URL, cover private mode where sessionStorage throws on write. An
 * in-memory timestamp is only a same-page backup; reload clears it.
 */
const readAttemptAt = (): number | null => {
  const session = readSession();
  if (session.available && session.value !== null) {
    return session.value;
  }
  return readCookie() ?? readUrl() ?? memoryAttemptAt;
};

/** True when this tab has not auto-reloaded for a chunk failure recently. */
export const canAttemptChunkLoadRecovery = (
  now = Date.now(),
  cooldownMs = CHUNK_LOAD_RECOVERY_COOLDOWN_MS
): boolean => {
  const last = readAttemptAt();
  if (last !== null) {
    return now - last >= cooldownMs;
  }
  return canPersistRecoveryMarker();
};

/** True when a reload-persistent recovery marker is already stored. */
export const hasChunkLoadRecoveryMarker = (): boolean =>
  readAttemptAt() !== null;

/**
 * Record that we are about to reload. Returns false when the marker cannot
 * be stored anywhere that survives `location.reload()`, so the caller must
 * not reload.
 */
export const markChunkLoadRecoveryAttempt = (now = Date.now()): boolean => {
  memoryAttemptAt = now;
  if (writeSession(now) || writeCookie(now) || writeUrl(now)) {
    return true;
  }
  memoryAttemptAt = null;
  return false;
};

/** Lets the Sentry test page demonstrate recovery again after a reload. */
export const clearChunkLoadRecoveryAttempt = (): void => {
  memoryAttemptAt = null;
  resetCapabilityCache();
  try {
    sessionStorage.removeItem(CHUNK_LOAD_RECOVERY_STORAGE_KEY);
  } catch {
    // sessionStorage can throw in private mode.
  }
  clearCookie();
  clearUrl();
};
